<svelte:options
  customElement={{
    tag: 'periode-table',
    shadow: 'none',
    props: {
      sourceUrl: { reflect: true, attribute: 'source-url' },
    },
  }}
/>

<script lang="ts">
  import { onMount, tick } from 'svelte';

  import { type VakkenOverzicht } from './types/vakkenoverzicht';
  import { afgelopen, formatDateRange, sleep } from './utils';

  const tableId = 'periodes';
  const shouldScrollIntoView = decodeURI(window.location.hash).substring(1).toLowerCase() === tableId;

  interface Props {
    sourceUrl: string;
  }
  const { sourceUrl }: Props = $props();

  let data: VakkenOverzicht = $state({
    cursussen: {
      havo: [],
      vwo: [],
    },
    periodes: [],
  });
  let loaded = $state(false);
  const loadData = async (signal: AbortSignal) => {
    const response = await fetch(sourceUrl, {
      headers: {
        Accept: 'application/json',
      },
      signal,
    });
    data = await response.json() as VakkenOverzicht;
    loaded = true;
  };

  let tableElement = $state<HTMLTableElement>();
  const scrollIntoView = async () => {
    // Ensure component is done rendering
    await tick();
    // Add some delay to 'ensure' the vakkenoverzicht components, which in practice are rendered above this component, are also done rendering
    await sleep(500);
    tableElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  };
  onMount(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    void loadData(signal).then(async () => {
      // If url hash references periode table, scroll it into view after loading is done
      if (shouldScrollIntoView) {
        await scrollIntoView();
      }
    });

    return () => {
      abortController.abort();
    };
  });
</script>

<div class="table-wrapper center" id="{tableId}">
  <table bind:this={tableElement} class="periodeoverzicht table" cellspacing="0" cellpadding="5" border="1">
    <thead>
      <tr>
        <th>Periode</th>
        <th>Dagen</th>
        <th>Opmerkingen</th>
      </tr>
    </thead>
    {#if loaded}
    <tbody>
      {#each data.periodes as periode (periode.Periode_ID)}
        <tr class="periode" class:afgelopen={afgelopen(periode)}>
          <td class="periodenaam">Periode { periode.naam }</td>
          <td>{ formatDateRange(periode.dagen) }</td>
          <td>{ periode.opmerkingen ?? '' }</td>
        </tr>
      {/each}
    </tbody>
    {/if}
  </table>
</div>

<style lang="scss">
  @use "./scss/variables";

  .table.periodeoverzicht {
    border: 0;
    border-collapse: collapse;
    font-family: variables.$font-family-serif;
    margin-bottom: 0;
    scroll-margin-top: 1.5rem; /* Ensure header (not included here) will also be visible on scrollintoview */
    table-layout: fixed;
  }

  td.periodenaam {
    font-weight: variables.$font-weight-bold;
    white-space: nowrap;
  }

  tr.periode {
    &:nth-of-type(2n+1) {
      background-color: variables.$white;
    }

    &.afgelopen {
      color: variables.$periode-afgelopen;
    }
  }
</style>
